import { supabaseAdmin, supabase } from './supabase'

/**
 * Admin service for operations that require elevated permissions
 * Uses service role to bypass RLS policies when needed
 */
export class AdminService {
  
  /**
   * Delete a user from the users table (admin only)
   * This bypasses RLS policies using the service role
   */
  static async deleteUser(userId: string, currentUserRole: string): Promise<boolean> {
    console.log('🚀 AdminService.deleteUser called with:', { userId, currentUserRole });
    console.log('🔑 Environment check:', {
      hasServiceKey: !!import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY,
      hasSupabaseAdmin: !!supabaseAdmin,
      serviceKeyLength: import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY?.length || 0
    });

    // Verify the current user is an admin
    if (currentUserRole !== 'admin') {
      console.error('❌ Only admin users can delete other users');
      alert('❌ Permission denied: Only admin users can delete other users');
      throw new Error('Insufficient permissions: Admin role required');
    }

    // Use service role client if available, otherwise fall back to regular client
    const client = supabaseAdmin || supabase;
    
    try {
      console.log('🗑️ Admin service: Attempting to delete user with ID:', userId);
      console.log('🔑 Using service role client:', !!supabaseAdmin);
      console.log('🔑 Client details:', client === supabaseAdmin ? 'SERVICE_ROLE' : 'ANON_KEY');

      const { data, error } = await client
        .from('users')
        .delete()
        .eq('id', userId)
        .select();

      console.log('📊 Delete operation result:', { data, error, deletedCount: data?.length || 0 });

      if (error) {
        console.error('❌ Admin service delete error:', error);
        alert(`❌ Delete failed: ${error.message}`);
        throw error;
      }

      if (!data || data.length === 0) {
        console.error('❌ No rows deleted - still a permissions issue');
        alert('❌ Delete failed: No rows were deleted. Still a permissions issue.');
        throw new Error('No rows deleted - permissions still blocking operation');
      }

      console.log('✅ Admin service: Successfully deleted user:', data);
      alert('✅ User successfully deleted from database!');
      return true;

    } catch (error) {
      console.error('❌ Admin service: Failed to delete user:', error);
      throw error;
    }
  }

  /**
   * Delete a journalist from the journalists table (admin only)
   */
  static async deleteJournalist(journalistId: string, currentUserRole: string): Promise<boolean> {
    if (currentUserRole !== 'admin') {
      throw new Error('Insufficient permissions: Admin role required');
    }

    const client = supabaseAdmin || supabase;
    
    try {
      const { error } = await client
        .from('journalists')
        .delete()
        .eq('id', journalistId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('❌ Admin service: Failed to delete journalist:', error);
      throw error;
    }
  }

  /**
   * Delete a guest from the celebrities table (admin only)
   */
  static async deleteGuest(guestId: string, currentUserRole: string): Promise<boolean> {
    if (currentUserRole !== 'admin') {
      throw new Error('Insufficient permissions: Admin role required');
    }

    const client = supabaseAdmin || supabase;
    
    try {
      const { error } = await client
        .from('celebrities')
        .delete()
        .eq('id', guestId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('❌ Admin service: Failed to delete guest:', error);
      throw error;
    }
  }
}